import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle,
  Clipboard,
  ClipboardCheck,
  Loader2,
  RefreshCw,
  ShieldCheck,
  Trash2,
  XCircle,
} from 'lucide-react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  deleteReviewedQuestion,
  getQuestionReviews,
  updateQuestion,
  updateQuestionReviewStatus,
} from '../lib/api';
import { formatDate, SUBJECTS } from '../lib/utils';
import { toast } from 'sonner';

const statusConfig = {
  pending: {
    label: 'Pendente',
    className: 'bg-amber-100 text-amber-800 border-amber-200',
  },
  corrected: {
    label: 'Corrigida',
    className: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  },
  kept: {
    label: 'Mantida',
    className: 'bg-blue-100 text-blue-800 border-blue-200',
  },
  ignored: {
    label: 'Ignorada',
    className: 'bg-zinc-100 text-zinc-700 border-zinc-200',
  },
  deleted: {
    label: 'Excluída',
    className: 'bg-red-100 text-red-800 border-red-200',
  },
};

const optionLabel = (option) => option ? option.toUpperCase() : 'Não informado';

const getSubjectName = (review) => (
  review.subject_name ||
  SUBJECTS[review.subject]?.name ||
  review.subject ||
  'Matéria não informada'
);

const AdminQuestionReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);

  const pendingCount = useMemo(
    () => reviews.filter((review) => review.status === 'pending').length,
    [reviews]
  );

  useEffect(() => {
    loadReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const loadReviews = async () => {
    setLoading(true);
    try {
      const response = await getQuestionReviews(statusFilter);
      setReviews(Array.isArray(response.data) ? response.data : []);
    } catch {
      toast.error('Erro ao carregar revisões');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (review, nextStatus) => {
    const officialExplanation = (review.official_explanation || '').trim();
    if (nextStatus === 'kept' && !officialExplanation) {
      toast.error('Escreva a explicação oficial antes de manter o gabarito');
      return;
    }

    setUpdatingId(review.id);
    try {
      const response = await updateQuestionReviewStatus(
        review.id,
        nextStatus,
        '',
        nextStatus === 'kept' ? officialExplanation : null
      );
      setReviews((current) => {
        if (statusFilter !== 'all' && nextStatus !== statusFilter) {
          return current.filter((item) => item.id !== review.id);
        }

        return current.map((item) => item.id === review.id ? response.data : item);
      });
      toast.success('Status da revisão atualizado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar revisão');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleOfficialExplanationChange = (reviewId, value) => {
    setReviews((current) => current.map((item) => (
      item.id === reviewId
        ? { ...item, official_explanation: value }
        : item
    )));
  };

  const handleCorrectOptionChange = async (review, nextOption) => {
    setUpdatingId(review.id);
    try {
      await updateQuestion(review.question_id, { correct_option: nextOption });
      const response = await updateQuestionReviewStatus(review.id, 'corrected');

      setReviews((current) => {
        if (statusFilter !== 'all' && statusFilter !== 'corrected') {
          return current.filter((item) => item.id !== review.id);
        }

        return current.map((item) => item.id === review.id ? response.data : item);
      });

      toast.success(`Gabarito alterado para ${nextOption.toUpperCase()} e revisão marcada como corrigida`);
    } catch {
      toast.error('Erro ao alterar gabarito');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDeleteQuestion = async (review) => {
    const confirmed = window.confirm(
      'Excluir e anular esta questão?\n\nEla não aparecerá em novos simulados. Nos simulados já realizados, todos os alunos receberão o ponto e poderão ver a nota original e a nota revisada.'
    );
    if (!confirmed) return;

    setUpdatingId(review.id);
    try {
      const response = await deleteReviewedQuestion(review.id);

      setReviews((current) => {
        if (statusFilter !== 'all' && statusFilter !== 'deleted') {
          return current.filter((item) => item.id !== review.id);
        }
        return current.map((item) => item.id === review.id ? response.data : item);
      });

      toast.success('Questão excluída e anulada; o ponto foi concedido a todos que a responderam');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir questão');
      await loadReviews();
    } finally {
      setUpdatingId(null);
    }
  };

  const copyQuestionId = async (questionId) => {
    try {
      await navigator.clipboard.writeText(questionId);
      toast.success('ID da questão copiado');
    } catch {
      toast.error('Não foi possível copiar o ID');
    }
  };

  const renderOptionSnapshot = (review, option) => {
    const text = review.options_snapshot?.[option] || '';
    const isReported = review.correct_option_reported === option;
    const isSuggested = review.suggested_correct_option === option;
    const isCurrent = review.current_correct_option === option;

    return (
      <div
        key={option}
        className="rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm"
      >
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-zinc-900">{option.toUpperCase()})</span>
          {isReported && <Badge className="bg-amber-100 text-amber-800 border-amber-200">Gabarito antigo</Badge>}
          {isCurrent && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Gabarito atual</Badge>}
          {isSuggested && <Badge className="bg-blue-100 text-blue-800 border-blue-200">Sugestão IA</Badge>}
        </div>
        <p className="mt-1 text-zinc-700 whitespace-pre-wrap">{text}</p>
      </div>
    );
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-zinc-900 rounded-lg flex items-center justify-center">
                <ShieldCheck className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-zinc-900">
                  Questões para Revisão
                </h1>
                <p className="text-zinc-500">
                  Sinalizações feitas pelo Agente V.O.A.R. quando o gabarito pode estar incorreto.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Filtrar status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pendentes</SelectItem>
                <SelectItem value="corrected">Corrigidas</SelectItem>
                <SelectItem value="kept">Mantidas</SelectItem>
                <SelectItem value="ignored">Ignoradas</SelectItem>
                <SelectItem value="deleted">Excluídas</SelectItem>
                <SelectItem value="all">Todas</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={loadReviews} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="rounded-lg border border-zinc-200 bg-white p-4">
            <p className="text-sm text-zinc-500">Filtro atual</p>
            <p className="text-2xl font-bold text-zinc-900">{reviews.length}</p>
          </div>
          <div className="rounded-lg border border-zinc-200 bg-white p-4">
            <p className="text-sm text-zinc-500">Pendentes nesta lista</p>
            <p className="text-2xl font-bold text-amber-600">{pendingCount}</p>
          </div>
          <div className="rounded-lg border border-zinc-200 bg-white p-4">
            <p className="text-sm text-zinc-500">Acesso</p>
            <p className="text-base font-semibold text-zinc-900">Somente administradores</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
          </div>
        ) : reviews.length === 0 ? (
          <div className="rounded-lg border border-dashed border-zinc-300 bg-white p-10 text-center">
            <ClipboardCheck className="w-10 h-10 text-zinc-400 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-zinc-900">Nenhuma revisão encontrada</h2>
            <p className="text-zinc-500 mt-1">
              Quando o Agente V.O.A.R. sinalizar uma questão, ela aparecerá aqui.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {reviews.map((review) => {
              const status = statusConfig[review.status] || statusConfig.pending;
              const isUpdating = updatingId === review.id;

              return (
                <div key={review.id} className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
                  <div className="p-4 lg:p-5 border-b border-zinc-100">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <Badge className={status.className}>{status.label}</Badge>
                          {review.changed_since_report && (
                            <Badge className="bg-purple-100 text-purple-800 border-purple-200">
                              Gabarito alterado
                            </Badge>
                          )}
                          {review.matches_suggestion && (
                            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                              Bate com sugestão da IA
                            </Badge>
                          )}
                          {!review.current_question_exists && (
                            <Badge className="bg-red-100 text-red-800 border-red-200">
                              Questão não encontrada
                            </Badge>
                          )}
                        </div>
                        <h2 className="text-lg font-bold text-zinc-900">{getSubjectName(review)}</h2>
                        <p className="text-xs text-zinc-500 mt-1">
                          Sinalizada em {formatDate(review.created_at)}
                        </p>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyQuestionId(review.question_id)}
                        >
                          <Clipboard className="w-4 h-4 mr-2" />
                          Copiar ID
                        </Button>
                        <Button asChild variant="outline" size="sm">
                          <Link to="/admin/questions">Abrir questões</Link>
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 lg:p-5 space-y-4">
                    <div className="rounded-lg bg-zinc-50 border border-zinc-200 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">
                        Enunciado sinalizado
                      </p>
                      <p className="text-zinc-900 whitespace-pre-wrap">{review.question_text_snapshot}</p>
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                      {['a', 'b', 'c', 'd'].map((option) => renderOptionSnapshot(review, option))}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <div className="rounded-lg border border-zinc-200 p-3">
                        <p className="text-xs text-zinc-500">Gabarito quando sinalizou</p>
                        <p className="text-xl font-bold text-amber-600">{optionLabel(review.correct_option_reported)}</p>
                      </div>
                      <div className="rounded-lg border border-zinc-200 p-3">
                        <p className="text-xs text-zinc-500">Gabarito atual</p>
                        <p className="text-xl font-bold text-emerald-600">{optionLabel(review.current_correct_option)}</p>
                      </div>
                      <div className="rounded-lg border border-zinc-200 p-3">
                        <p className="text-xs text-zinc-500">Sugestão IA</p>
                        <p className="text-xl font-bold text-blue-600">{optionLabel(review.suggested_correct_option)}</p>
                      </div>
                      <div className="rounded-lg border border-zinc-200 p-3">
                        <p className="text-xs text-zinc-500">Aluno marcou</p>
                        <p className="text-xl font-bold text-zinc-900">{optionLabel(review.selected_option)}</p>
                      </div>
                    </div>

                    <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-amber-900">Motivo técnico apontado pela IA</p>
                          <p className="mt-1 text-sm text-amber-900 whitespace-pre-wrap">
                            {review.review_reason || 'Sem justificativa registrada.'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {review.current_question_exists && (
                      <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4">
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                          <div>
                            <p className="font-semibold text-zinc-900">Alterar gabarito diretamente</p>
                            <p className="text-sm text-zinc-500">
                              Ao escolher uma alternativa, o gabarito da questão será atualizado e a revisão será marcada como corrigida.
                            </p>
                          </div>
                          <div className="grid grid-cols-4 gap-2 sm:flex sm:flex-wrap">
                            {['a', 'b', 'c', 'd'].map((option) => (
                              <Button
                                key={option}
                                type="button"
                                size="sm"
                                variant={review.current_correct_option === option ? 'default' : 'outline'}
                                disabled={isUpdating}
                                onClick={() => handleCorrectOptionChange(review, option)}
                                className="min-w-12"
                              >
                                {option.toUpperCase()}
                              </Button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {review.current_question_exists && (
                      <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-4">
                        <div className="mb-3">
                          <p className="font-semibold text-zinc-900">
                            Explicação oficial para os alunos
                          </p>
                          <p className="mt-1 text-sm text-zinc-600">
                            Este texto substituirá a resposta contestada da IA e será exibido para
                            todos os alunos, independentemente da alternativa marcada.
                          </p>
                        </div>
                        <Textarea
                          value={review.official_explanation || ''}
                          onChange={(event) => handleOfficialExplanationChange(
                            review.id,
                            event.target.value
                          )}
                          disabled={isUpdating}
                          rows={8}
                          placeholder={'O gabarito correto é ... porque ...\n\nA alternativa A está incorreta porque ...\nA alternativa B ...'}
                          className="bg-white"
                        />
                        <div className="mt-2 flex flex-col gap-1 text-xs text-zinc-500 sm:flex-row sm:items-center sm:justify-between">
                          <span>
                            Explique o gabarito e, de preferência, todas as alternativas incorretas.
                          </span>
                          <span>{(review.official_explanation || '').length} caracteres</span>
                        </div>
                        {review.status === 'kept' && review.official_explanation_published_at && (
                          <p className="mt-3 text-xs font-medium text-blue-700">
                            Publicada em {formatDate(review.official_explanation_published_at)}
                            {review.reviewed_by ? ` por ${review.reviewed_by}` : ''}.
                          </p>
                        )}
                      </div>
                    )}

                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 pt-2">
                      <p className="text-sm text-zinc-500">
                        Marque manualmente depois de revisar a questão na base.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          disabled={isUpdating}
                          onClick={() => handleStatusChange(review, 'corrected')}
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Marcar corrigida
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={isUpdating || !(review.official_explanation || '').trim()}
                          onClick={() => handleStatusChange(review, 'kept')}
                        >
                          <ShieldCheck className="w-4 h-4 mr-2" />
                          Manter gabarito
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={isUpdating}
                          onClick={() => handleStatusChange(review, 'ignored')}
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Ignorar
                        </Button>
                        {review.current_question_exists && (
                          <Button
                            size="sm"
                            variant="destructive"
                            disabled={isUpdating}
                            onClick={() => handleDeleteQuestion(review)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Excluir questão
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminQuestionReviewsPage;
